/**
 * Executive question library — the "What would you like to know?" screen.
 *
 * One curated library, grouped by category, ordered by (category,
 * sort_order) upstream. Items are Trinity-authored, pre-parameterized
 * prompts; clicking one runs a deterministic Chart Visualization — no
 * free-text input exists on this surface by design.
 *
 * QUESTIONS | RECENT (n) tab control (design doc executive-view-design/
 * EXEC_WHATIF_v1_design.md §5, mirroring What-If HOME's grammar — see
 * WhatIf/HomeState.tsx's header comment for the shared row treatment and
 * small-caps conventions this borrows). The tab control is hidden
 * entirely when there are no recents — zero-recents QF renders pixel-
 * identical to the pre-Task-16 library (categorized QUESTIONS body,
 * unchanged). QUESTIONS is always the default tab (ruled 2026-08-23).
 * The tab value is NOT owned here: this component unmounts/remounts on
 * every library ↔ answer-view transition (index.tsx renders it only
 * when `activeBrand && !activePrompt`), so any local tab state would
 * reset on every round trip through an answer — the same Task 12
 * round-1 lesson WhatIfView's homeTab comment documents. index.tsx
 * lifts `qfTab` instead, exactly like WhatIfView holds HomeState's.
 *
 * A RECENT row carries prompt_id + title + scenario_names (display
 * metadata only) — the full prompt object is resolved from `prompts` by
 * id here at click time. If the prompt no longer exists (deleted or
 * disabled since the run), the row still renders but clicking it shows a
 * calm inline note instead of calling onSelect — never a crash. Recorded
 * scenarios are never applied to the click (late-binding rule, §5): the
 * click calls `onSelect(prompt)` exactly like a QUESTIONS row, without
 * touching scenarioIds.
 *
 * 2026-09-07 in-app pass rulings: row titles are regular weight (the all-bold list read as "too much"; the What-If
 * examples list was the model), titles render their **bold** markup through renderPromptTitle so authors can
 * emphasize the entities in a question, and a search field filters the QUESTIONS tab client-side (title + subtitle,
 * case-insensitive). Recents are not searched — a handful of rows at most.
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowForward, Search } from '@mui/icons-material';

import {
  renderPromptTitle,
  stripPromptMarkdown,
} from 'src/components/shared/PromptTitle';

import { formatUtcTimestamp } from './formatUtcTimestamp';
import { EXEC_CONTENT_CONTAINER_SX } from './layout';
import { ExecutiveQueryRecentRun, ExecutivePromptItem } from './types';

export type QFTab = 'questions' | 'recent';

interface QuestionLibraryProps {
  prompts: ExecutivePromptItem[];
  loading: boolean;
  error: string | null;
  onSelect: (prompt: ExecutivePromptItem) => void;
  recents: ExecutiveQueryRecentRun[];
  // Controlled by index.tsx (see file header) — this component neither
  // stores nor defaults the tab itself.
  activeTab: QFTab;
  onChangeTab: (tab: QFTab) => void;
  // Copy overrides so the tornado destination reuses this whole screen
  // (grouping, cards, tabs, recents resolution) with its own wording.
  // Defaults preserve the Query Forecast surface verbatim.
  heading?: string;
  subheading?: string;
  emptyCopy?: string;
  searchPlaceholder?: string;
}

const SECTION_LABEL_SX = {
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  fontWeight: 600,
} as const;

const matchesQuery = (prompt: ExecutivePromptItem, needle: string): boolean =>
  stripPromptMarkdown(prompt.title).toLowerCase().includes(needle) ||
  (prompt.subtitle ?? '').toLowerCase().includes(needle);

const QuestionLibrary: React.FC<QuestionLibraryProps> = ({
  prompts,
  loading,
  error,
  onSelect,
  recents,
  activeTab,
  onChangeTab,
  heading = 'What would you like to know?',
  subheading = 'Pick a question',
  emptyCopy = 'No questions are configured for this brand yet. Please contact your Trinity team.',
  searchPlaceholder = 'Search questions',
}) => {
  const hasRecents = recents.length > 0;
  const effectiveTab = hasRecents ? activeTab : 'questions';

  // Set when a RECENT row's prompt can't be resolved from the current
  // prompts list (deleted/disabled since the run) — calm inline note,
  // never a crash (see file header).
  const [unavailablePromptId, setUnavailablePromptId] = useState<number | null>(
    null
  );

  // QUESTIONS-tab search (2026-09-07). Local state is fine here even though the component remounts per answer round
  // trip (see header) — a cleared search on return is the expected behaviour, unlike the tab choice.
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();

  const promptsById = useMemo(() => {
    const byId = new Map<number, ExecutivePromptItem>();
    prompts.forEach((prompt) => byId.set(prompt.id, prompt));
    return byId;
  }, [prompts]);

  const grouped = useMemo(() => {
    const byCategory = new Map<string, ExecutivePromptItem[]>();
    prompts
      .filter((prompt) => !needle || matchesQuery(prompt, needle))
      .forEach((prompt) => {
        const existing = byCategory.get(prompt.category);
        if (existing) {
          existing.push(prompt);
        } else {
          byCategory.set(prompt.category, [prompt]);
        }
      });
    return Array.from(byCategory.entries());
  }, [prompts, needle]);

  const handleSelectRecent = (recent: ExecutiveQueryRecentRun) => {
    const resolved = promptsById.get(recent.prompt_id);
    if (!resolved) {
      setUnavailablePromptId(recent.prompt_id);
      return;
    }
    setUnavailablePromptId(null);
    onSelect(resolved);
  };

  const questionsReady = !loading && !error && prompts.length > 0;
  const showSearch = questionsReady && effectiveTab === 'questions';

  return (
    <Box sx={{ ...EXEC_CONTENT_CONTAINER_SX, py: 5 }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        {heading}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: hasRecents || showSearch ? 2 : 4 }}
      >
        {subheading}
      </Typography>

      {/* Tabs on the left, search on the right; either may be absent. */}
      {(hasRecents || showSearch) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          {hasRecents ? (
            <Tabs
              value={effectiveTab}
              onChange={(_e, next: QFTab) => onChangeTab(next)}
              sx={{ minHeight: 36 }}
            >
              <Tab
                value="questions"
                label="Questions"
                sx={{ minHeight: 36, py: 0.5 }}
              />
              <Tab
                value="recent"
                label={`Recent (${recents.length})`}
                sx={{ minHeight: 36, py: 0.5 }}
              />
            </Tabs>
          ) : (
            <Box />
          )}
          {showSearch && (
            <TextField
              size="small"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              inputProps={{ 'aria-label': searchPlaceholder }}
              sx={{ width: 320, maxWidth: '100%' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>
      )}

      {effectiveTab === 'questions' && loading && (
        <Typography variant="body1" color="text.secondary">
          Loading questions…
        </Typography>
      )}
      {effectiveTab === 'questions' && !loading && error && (
        <Typography variant="body1" color="text.secondary">
          {error}
        </Typography>
      )}
      {effectiveTab === 'questions' &&
        !loading &&
        !error &&
        prompts.length === 0 && (
          <Typography variant="body1" color="text.secondary">
            {emptyCopy}
          </Typography>
        )}
      {effectiveTab === 'questions' &&
        questionsReady &&
        needle &&
        grouped.length === 0 && (
          <Typography variant="body1" color="text.secondary">
            No questions match &ldquo;{query.trim()}&rdquo;.
          </Typography>
        )}

      {effectiveTab === 'questions' &&
        !loading &&
        !error &&
        grouped.map(([category, items]) => (
          <Box key={category} sx={{ mb: 4 }}>
            {/* Section-label treatment, standardized with the sidebar's
                SCENARIOS label (2026-08-16 font audit). */}
            <Typography
              variant="caption"
              component="div"
              color="text.secondary"
              sx={{ ...SECTION_LABEL_SX, mb: 1.5 }}
            >
              {category}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {items.map((prompt) => (
                <Card key={prompt.id} variant="outlined">
                  <CardActionArea onClick={() => onSelect(prompt)}>
                    <CardContent
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 1.5,
                        '&:last-child': { pb: 1.5 },
                      }}
                    >
                      <Box>
                        <Typography variant="body1">
                          {renderPromptTitle(prompt.title)}
                        </Typography>
                        {prompt.subtitle && (
                          <Typography variant="body2" color="text.secondary">
                            {prompt.subtitle}
                          </Typography>
                        )}
                      </Box>
                      <ArrowForward
                        fontSize="small"
                        sx={{ color: 'text.secondary', ml: 2, flexShrink: 0 }}
                      />
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Box>
          </Box>
        ))}

      {effectiveTab === 'recent' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {recents.map((recent) => (
            <Box key={recent.prompt_id}>
              <Card variant="outlined">
                <CardActionArea onClick={() => handleSelectRecent(recent)}>
                  <CardContent
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1.5,
                      '&:last-child': { pb: 1.5 },
                    }}
                  >
                    <Box>
                      <Typography variant="body1">
                        {renderPromptTitle(recent.title)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {recent.scenario_names.join(', ')}
                        {' · '}
                        {formatUtcTimestamp(recent.inserted_at)}
                      </Typography>
                    </Box>
                    <ArrowForward
                      fontSize="small"
                      sx={{ color: 'text.secondary', ml: 2, flexShrink: 0 }}
                    />
                  </CardContent>
                </CardActionArea>
              </Card>
              {unavailablePromptId === recent.prompt_id && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, ml: 0.5 }}
                >
                  This question is no longer available.
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default QuestionLibrary;
